import { Locale } from "next/router";

import {
  getHelpCategoryPath,
  getHelpPath,
} from "@/common/utils/help/help-url.utils";
import { locales, toContentLocale } from "@/i18n/config";

export type HelpCategoryView = {
  id: string;
  title: string;
  slug: string;
  order: number;
  articleCount: number;
  description?: string | null;
  children: HelpCategoryView[];
};

type HelpCategorySource = {
  id: string;
  title: string;
  slug: string;
  order: number;
  articleCount: number;
  description?: string | null;
  children?: readonly HelpCategorySource[] | null;
};

const normalizeHelpTitle = (title: string) =>
  title.trim().replace(/\s+/g, " ").toLowerCase();

export const toHelpCategoryView = (
  category: HelpCategorySource
): HelpCategoryView => ({
  id: category.id,
  title: category.title,
  slug: category.slug,
  order: category.order,
  articleCount: category.articleCount,
  description: category.description,
  children: (category.children ?? []).map(toHelpCategoryView),
});

export const flattenHelpCategories = (
  categories: HelpCategoryView[]
): HelpCategoryView[] =>
  categories.flatMap(category => [
    category,
    ...flattenHelpCategories(category.children),
  ]);

export const findHelpCategory = (
  categories: HelpCategoryView[],
  slug: string
): HelpCategoryView | undefined =>
  flattenHelpCategories(categories).find(category => category.slug === slug);

export const findHelpCategoryById = (
  categories: HelpCategoryView[],
  id: string
): HelpCategoryView | undefined =>
  flattenHelpCategories(categories).find(category => category.id === id);

export const filterEmptyHelpCategories = (
  categories: HelpCategoryView[]
): HelpCategoryView[] =>
  categories
    .map(category => ({
      ...category,
      children: filterEmptyHelpCategories(category.children),
    }))
    .filter(category => category.articleCount > 0);

const shouldCollapseWithSingleChild = (
  parent: HelpCategoryView,
  child: HelpCategoryView
) => {
  if (normalizeHelpTitle(parent.title) === normalizeHelpTitle(child.title)) {
    return true;
  }

  // Parent counts include descendants. Equal counts mean the parent has no
  // articles of its own, so "Returns > Return" is the same node.
  return parent.articleCount === child.articleCount;
};

export const collapseRedundantHelpCategories = (
  categories: HelpCategoryView[]
): HelpCategoryView[] =>
  categories.map(category => {
    const children = collapseRedundantHelpCategories(category.children);

    if (
      children.length === 1 &&
      children[0] &&
      shouldCollapseWithSingleChild(category, children[0])
    ) {
      const child = children[0];

      return {
        ...child,
        title: category.title,
        children: child.children,
      };
    }

    return {
      ...category,
      children,
    };
  });

export const presentHelpCategories = (
  categories: HelpCategoryView[]
): HelpCategoryView[] =>
  collapseRedundantHelpCategories(filterEmptyHelpCategories(categories));

export const getHelpOverviewHreflangUrls = (): Record<Locale, string> =>
  Object.fromEntries(locales.map(locale => [locale, getHelpPath()])) as Record<
    Locale,
    string
  >;

export const getHelpCategoryHreflangUrls = (
  treesByLocale: Partial<Record<string, HelpCategoryView[]>>,
  categoryId: string
): Record<string, string> => {
  const urls: Record<string, string> = {};

  for (const locale of locales) {
    const tree =
      treesByLocale[locale] ?? treesByLocale[toContentLocale(locale)] ?? [];
    const match = findHelpCategoryById(tree, categoryId);

    if (match) {
      urls[locale] = getHelpCategoryPath(match.slug);
    }
  }

  return urls;
};

export const getSiblingsForCategory = (
  categories: HelpCategoryView[],
  slug: string
): HelpCategoryView[] => {
  const presented = presentHelpCategories(categories);

  if (presented.some(category => category.slug === slug)) {
    return presented.filter(category => category.slug !== slug);
  }

  for (const parent of flattenHelpCategories(presented)) {
    if (parent.children.some(child => child.slug === slug)) {
      return parent.children.filter(child => child.slug !== slug);
    }
  }

  return [];
};
